// Purchase records list — Receive Stock List / On-Hold Receive Stock List.
//
//   <purchase-records .storeCode=... kind="receive|hold">
//
// Actions: view detail (lines), print PDF (GetPurchaseOrderInPDF), edit
// (posted rows → "trade-edit" event the hub turns into a Receive Stock load
// that saves back to the same record), recall (holds → "trade-recall" event
// the hub turns into a Receive Stock load), delete (confirm; server enforces
// permissions). Posted lists default to today; on-hold lists show all
// (desktop parity).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { TradePurchaseRow } from '../lib/api.js';
import { OutletViewCache, invalidateOutletView } from '../lib/outlet-view-cache.js';
import { serverEvents, debounce, LIST_CHANGE_CODES, type ServerEvent } from '../lib/events.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface KeptPurchaseView {
  from: string;
  to: string;
  rows: TradePurchaseRow[];
  error: string;
  notice: string;
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const dayStartMs = (iso: string) => new Date(`${iso}T00:00:00`).getTime();
const dayEndMs = (iso: string) => new Date(`${iso}T23:59:59.999`).getTime();

const fmtDate = (ms: number) => (ms ? new Date(ms).toLocaleString() : '—');
const fmtMoney = (n: number | undefined) => (Number(n) || 0).toFixed(2);

@customElement('purchase-records')
export class PurchaseRecords extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.6rem; padding: 0.75rem 1rem 0.6rem; flex-shrink: 0; }
    .toolbar .fg { margin-bottom: 0; }
    .spacer { flex: 1; }
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0 1rem 1rem; }
    .table-wrap { flex: 1; min-height: 0; overflow: auto; border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); }
    .table-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }
    .row-actions { display: flex; gap: 0.3rem; justify-content: flex-end; }
    .empty { padding: 2rem 1rem; text-align: center; color: var(--color-text-secondary); font-size: 0.85rem; }
    .notice { margin: 0 1rem 0.5rem; font-size: 0.8rem; color: var(--color-success, #188038); }
    .err { margin: 0 1rem 0.5rem; font-size: 0.8rem; color: var(--color-error, #d93025); }
    .hold-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #fff3e0; color: #b26a00; font-size: 0.68rem; font-weight: 700; }

    .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; }
    .modal { background: var(--color-surface); border-radius: 16px; width: 100%; max-width: 860px; max-height: 88dvh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 24px 60px rgba(15,35,80,0.3); }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: none; border: none; border-radius: 10px; font-size: 1.2rem; cursor: pointer; color: var(--color-text-secondary); }
    .modal-close:hover { background: #f4f7fb; }
    .modal-body { padding: 1rem 1.1rem; overflow-y: auto; }
    .modal-footer { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.5rem 1rem; margin-bottom: 1rem; }
    .detail-grid .lbl { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-secondary); }
    .detail-grid .val { font-size: 0.86rem; }
    .lines-wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; max-height: 340px; }
    .totals { display: flex; justify-content: flex-end; margin-top: 0.7rem; font-size: 0.95rem; font-weight: 700; }
    @media (max-width: 768px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh; }
      .modal-footer { padding-bottom: calc(0.9rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) kind: 'receive' | 'hold' = 'receive';

  @state() private _from = todayIso();
  @state() private _to = todayIso();
  @state() private _rows: TradePurchaseRow[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _notice = '';
  @state() private _detail: TradePurchaseRow | null = null;
  @state() private _detailBusy = false;
  /** Store setting "Allow editing of purchase records" (posted receives only). */
  @state() private _allowEdit = true;
  private _skStore = '';

  private _eventUnsubs: Array<() => void> = [];
  private _reloadSoon = debounce(() => void this._load(), 800);
  /** Server signalled a change that a completed load has not applied yet. */
  private _staleAfterEvent = false;

  private _view = new OutletViewCache<KeptPurchaseView>(this, 'trade-purchases', {
    capture: () => ({ from: this._from, to: this._to, rows: this._rows, error: this._error, notice: this._notice }),
    restore: (v) => {
      this._from = v.from;
      this._to = v.to;
      this._rows = v.rows;
      this._error = v.error;
      this._notice = v.notice;
      this._loading = false;
      this._detail = null;
    },
    reset: () => {
      this._from = todayIso();
      this._to = todayIso();
      this._rows = [];
      this._error = '';
      this._notice = '';
      this._loading = false;
      this._detail = null;
    },
  });

  connectedCallback() {
    super.connectedCallback();
    // Live list refresh — same behaviour as the desktop purchase windows
    // (ServerEventMapping purchase/purchaseHold → wpEVT_DB_RELOAD_*_INVOICE_LIST):
    // only this outlet's events, only codes that change the rows.
    this._eventUnsubs = [
      serverEvents.on('purchase', this._onServerEvent),
      serverEvents.on('purchaseHold', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reloadSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    this._view.dispose();
    // A change event was seen but its debounced reload never ran (the user
    // left the tab first): do not keep the pre-event rows as this outlet's
    // kept view — the next visit should refetch.
    if (this._staleAfterEvent) {
      this._staleAfterEvent = false;
      invalidateOutletView('trade-purchases', `${this.storeCode}|${this.kind}`);
    }
  }

  /** ppServer data-change event — reload when this outlet's list changed. */
  private _onServerEvent = (ev: ServerEvent) => {
    if (ev.store && ev.store !== this.storeCode) return;
    if (ev.typeName !== (this.kind === 'hold' ? 'purchaseHold' : 'purchase')) return;
    if (!LIST_CHANGE_CODES.has(ev.code)) return;
    this._staleAfterEvent = true;
    this._reloadSoon();
  };

  /** Socket (re)connected or ppServer came back — events may have been missed. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync' || !this.storeCode) return;
    this._staleAfterEvent = true;
    this._reloadSoon();
  };

  protected updated() {
    const result = this._view.switchTo(this.storeCode ? `${this.storeCode}|${this.kind}` : '');
    if (this.storeCode && this._skStore !== this.storeCode) void this._ensureServerKey();
    if (result === 'unchanged') return;
    this._reloadSoon.cancel();
    this._staleAfterEvent = false;
    if (result === 'fresh') void this._load();
  }

  /** Read the outlet's "allow editing purchase records" flag (desktop parity). */
  private async _ensureServerKey() {
    const store = this.storeCode;
    if (!store) return;
    try {
      const sk = (await api.tradeServerKey(store)).data;
      if (this.storeCode !== store) return;
      this._allowEdit = sk.purchase?.allowEditInvoiceRecord !== false;
      this._skStore = store;
    } catch {
      // Keep the default (allow) — the store enforces its own rules on save.
      // Mark the outlet as attempted so every render does not retry the call.
      if (this.storeCode === store) this._skStore = store;
    }
  }

  private get _canEditPosted(): boolean {
    return this._allowEdit;
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    this._notice = '';
    try {
      const res = await api.tradePurchaseList(this.storeCode, {
        kind: this.kind,
        from: this.kind === 'hold' ? 0 : dayStartMs(this._from),
        to: this.kind === 'hold' ? 32472144000000 - 1 : dayEndMs(this._to),
      });
      if (!this._view.isCurrent(epoch)) return;
      this._rows = res.data ?? [];
      this._staleAfterEvent = false;
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private async _openDetail(row: TradePurchaseRow) {
    const epoch = this._view.epoch;
    this._detailBusy = true;
    this._error = '';
    try {
      const res = await api.tradePurchaseDetail(this.storeCode, row.id, this.kind);
      if (!this._view.isCurrent(epoch)) return;
      this._detail = res.data;
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load detail';
    } finally {
      if (this._view.isCurrent(epoch)) this._detailBusy = false;
    }
  }

  private async _downloadPdf(row: TradePurchaseRow) {
    const epoch = this._view.epoch;
    try {
      // Holds render as a DRAFT invoice; posted receives as the supplier invoice.
      const blob = await api.tradePurchasePdf(this.storeCode, row.id, this.kind === 'hold' ? 'draft' : 'invoice');
      if (!this._view.isCurrent(epoch)) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receive_${row.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'PDF failed';
    }
  }

  /** Recall an on-hold receive into the Receive Stock screen. */
  private _recall(row: TradePurchaseRow) {
    this._loadIntoEntry(row, false);
  }

  /** Edit a posted receive (holds have no Edit — use Recall). */
  private _edit(row: TradePurchaseRow) {
    this._loadIntoEntry(row, this.kind === 'receive');
  }

  private _loadIntoEntry(row: TradePurchaseRow, edit: boolean) {
    // List rows carry no lines — fetch the full record before handing it to
    // the Receive Stock screen (the detail modal already has them).
    // Posted edits use "trade-edit" so the entry saves back to the same id;
    // holds use "trade-recall" (new record, old hold released after success).
    const eventName = edit ? 'trade-edit' : 'trade-recall';
    const dispatch = (full: TradePurchaseRow) => this.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: { tradeKind: 'purchase', row: full, edit },
    }));
    if (row.lines && row.lines.length > 0) {
      dispatch(row);
      return;
    }
    void (async () => {
      const epoch = this._view.epoch;
      try {
        const res = await api.tradePurchaseDetail(this.storeCode, row.id, this.kind);
        if (!this._view.isCurrent(epoch)) return;
        dispatch(res.data);
      } catch (e: any) {
        if (!this._view.isCurrent(epoch)) return;
        this._error = e?.message || (edit ? 'Failed to load the record' : 'Failed to load the hold');
      }
    })();
  }

  private async _delete(row: TradePurchaseRow) {
    const label = this.kind === 'hold' ? `on-hold receive #${row.id}` : `receive #${row.id}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.tradeDeletePurchase(this.storeCode, row.id, this.kind);
      if (!this._view.isCurrent(epoch)) return;
      this._notice = `Deleted ${label}`;
      this._detail = null;
      void this._load();
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Delete failed';
    }
  }

  render() {
    return html`
      <div class="toolbar">
        ${this.kind === 'receive' ? html`
          <div class="fg">
            <label>From</label>
            <input type="date" .value=${this._from} ?disabled=${this._loading}
              @change=${(e: Event) => { this._from = (e.target as HTMLInputElement).value; }}>
          </div>
          <div class="fg">
            <label>To</label>
            <input type="date" .value=${this._to} ?disabled=${this._loading}
              @change=${(e: Event) => { this._to = (e.target as HTMLInputElement).value; }}>
          </div>
        ` : html`<span class="hold-badge">On hold — showing all</span>`}
        <button class="btn btn-sm" ?disabled=${this._loading} @click=${() => void this._load()}>
          ${this._loading ? 'Loading…' : '↻ Reload'}
        </button>
        <span class="spacer"></span>
      </div>
      ${this._error ? html`<div class="err">⚠ ${this._error}</div>` : ''}
      ${this._notice ? html`<div class="notice">✓ ${this._notice}</div>` : ''}

      <div class="tab-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Ref</th>
                <th>Supplier</th>
                <th class="num">Items</th>
                <th class="num">Total</th>
                <th style="width:1%"></th>
              </tr>
            </thead>
            <tbody>
              ${this._rows.length === 0 && !this._loading ? html`
                <tr><td colspan="7"><div class="empty">No records.</div></td></tr>
              ` : this._rows.map((row) => html`
                <tr class="clickable" data-testid="purchase-row" data-row-id=${row.id} @click=${() => void this._openDetail(row)}>
                  <td>${row.id}</td>
                  <td>${fmtDate(row.date)}</td>
                  <td>${row.refNo || '—'}</td>
                  <td>${row.supplierName || '—'}</td>
                  <td class="num">${row.itemCount}</td>
                  <td class="num">${fmtMoney(row.total)}</td>
                  <td @click=${(e: Event) => e.stopPropagation()}>
                    <div class="row-actions">
                      ${this.kind === 'receive' && this._canEditPosted ? html`
                        <button class="btn btn-sm" @click=${() => this._edit(row)}>Edit</button>
                      ` : ''}
                      ${this.kind === 'hold' ? html`
                        <button class="btn btn-sm" @click=${() => this._recall(row)}>Recall</button>
                      ` : ''}
                      <button class="btn btn-sm" @click=${() => void this._downloadPdf(row)}>PDF</button>
                      <button class="btn btn-sm btn-danger" @click=${() => void this._delete(row)}>Del</button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </div>

      ${this._detail || this._detailBusy ? this._renderDetail() : ''}
    `;
  }

  private _renderDetail() {
    const d = this._detail;
    return html`
      <div class="modal-overlay" @click=${() => { if (!this._detailBusy) this._detail = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>
              Receive ${d ? `#${d.id}` : ''}
              ${this.kind === 'hold' ? html`<span class="hold-badge" style="margin-left:0.4rem">On hold</span>` : ''}
            </h3>
            <button class="modal-close" @click=${() => { this._detail = null; }}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._detailBusy ? html`<div class="empty">Loading…</div>` : !d ? '' : html`
              <div class="detail-grid">
                <div><div class="lbl">Date</div><div class="val">${fmtDate(d.date)}</div></div>
                <div><div class="lbl">Supplier</div><div class="val">${d.supplierName || '—'}</div></div>
                <div><div class="lbl">Ref</div><div class="val">${d.refNo || '—'}</div></div>
                <div><div class="lbl">Term</div><div class="val">${d.term || 0}</div></div>
                <div><div class="lbl">Sales Rep</div><div class="val">${d.salesRepName || '—'}</div></div>
                ${d.note ? html`<div><div class="lbl">Note</div><div class="val">${d.note}</div></div>` : ''}
              </div>
              <div class="lines-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th><th>Item</th><th class="num">Qty</th><th>Pack</th>
                      <th class="num">Bonus</th><th class="num">Cost</th><th class="num">Disc</th>
                      <th>Batch</th><th>Expiry</th><th class="num">S/Price</th><th class="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(d.lines ?? []).map((l) => html`
                      <tr>
                        <td>${l.code}</td>
                        <td>${l.name}</td>
                        <td class="num">${l.quantity}</td>
                        <td>${l.packDesc || l.pack}</td>
                        <td class="num">${l.bonusQty || ''}</td>
                        <td class="num">${fmtMoney(l.price)}</td>
                        <td class="num">${l.discount ? fmtMoney(l.discount) : ''}</td>
                        <td>${l.batchNo || '—'}</td>
                        <td>${l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : '—'}</td>
                        <td class="num">${l.srp ? fmtMoney(l.srp) : ''}</td>
                        <td class="num">${fmtMoney(l.value)}</td>
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
              <div class="totals">Total ${fmtMoney(d.total)}</div>
            `}
          </div>
          <div class="modal-footer">
            ${d && this.kind === 'receive' && this._canEditPosted ? html`
              <button class="btn btn-sec" @click=${() => { this._detail = null; this._edit(d); }}>✎ Edit</button>
            ` : ''}
            ${d && this.kind === 'hold' ? html`
              <button class="btn btn-sec" @click=${() => { this._detail = null; this._recall(d); }}>Recall</button>
            ` : ''}
            ${d ? html`
              <button class="btn btn-sec" @click=${() => void this._downloadPdf(d)}>⬇ PDF</button>
              <button class="btn btn-danger" @click=${() => void this._delete(d)}>Delete</button>
            ` : ''}
            <button class="btn-cancel" @click=${() => { this._detail = null; }}>Close</button>
          </div>
        </div>
      </div>
    `;
  }
}
